// src/Components/StaffMyProfile.js
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import profileIcon from "../../Assets/DashboardAssets/profileIcon.png";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { ImSpinner3 } from "react-icons/im";
import { LuSchool } from "react-icons/lu";
import Layout from "./Layout";
import DashLayout from "../Admin/AdminDashLayout";
import { updatePasswordThunk } from "../../Store/Slices/Common/User/actions/userActions";

const StaffMyProfile = () => {
  const { userDetails } = useSelector((store) => store.common.user);
  const dispatch = useDispatch();

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [errors, setErrors] = useState({});

  const toggleShowPassword = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validatePasswords = () => {
    const newErrors = {};
    if (!passwordData.currentPassword.trim())
      newErrors.currentPassword = "Current password is required.";
    if (!passwordData.newPassword.trim())
      newErrors.newPassword = "New password is required.";
    if (!passwordData.confirmPassword.trim())
      newErrors.confirmPassword = "Please confirm your new password.";
    if (
      passwordData.newPassword &&
      passwordData.confirmPassword &&
      passwordData.newPassword !== passwordData.confirmPassword
    )
      newErrors.confirmPassword = "Passwords do not match.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const updatePassword = () => {
    if (!validatePasswords()) {
      toast.error("Please fix the errors.");
      return;
    }

    setLoading(true);
    dispatch(updatePasswordThunk(passwordData))
      .then(() => {
        toast.success("Password updated successfully!");
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        setShowPasswordModal(false);
      })
      .catch((err) => toast.error(err.message || "Failed to update password"))
      .finally(() => setLoading(false));
  };

  return (
    <Layout>
      <DashLayout>
        <div className="flex flex-col w-full p-6 gap-6">
          <div className="flex flex-col md:flex-row items-center gap-6 p-6 bg-white rounded-xl shadow-lg border border-gray-200">
            <img
              src={userDetails?.profile || profileIcon}
              alt="Profile"
              className="w-24 h-24 md:w-28 md:h-28 rounded-full shadow-xl border-2 border-gray-100"
            />
            <div className="flex-1 flex flex-col md:flex-row justify-between w-full gap-6">
              <div className="flex flex-col">
                <h2 className="text-2xl font-bold text-gray-900">
                  {userDetails?.fullName}
                </h2>
                <div className="flex items-center text-gray-600 mt-1 gap-2 text-lg">
                  <LuSchool className="text-pink-500" />
                  {userDetails?.schoolName}
                </div>
              </div>
              <button
                className={`px-6 py-2 h-12 text-sm font-semibold rounded-lg shadow-md ${
                  userDetails?.active
                    ? "bg-gradient-to-r from-pink-500 to-purple-500 text-white"
                    : "bg-gray-300 text-black"
                }`}
              >
                {userDetails?.active ? "Active" : "Deactive"}
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-gray-200 rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow duration-300">
              <h3 className="text-lg font-semibold mb-5 text-purple-600 border-b">
                Personal Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-10 text-gray-700">
                <div>
                  <span className="text-gray-900 block mb-1">Full Name</span>
                  <div className="font-medium text-gray-400">
                    {userDetails?.fullName || "N/A"}
                  </div>
                </div>
                <div>
                  <span className="text-gray-900 block mb-1">Email</span>
                  <div className="font-medium text-gray-400">
                    {userDetails?.email || "N/A"}
                  </div>
                </div>
                <div>
                  <span className="text-gray-900 block mb-1">Mobile</span>
                  <div className="font-medium text-gray-400">
                    {userDetails?.mobileNumber || "N/A"}
                  </div>
                </div>
                <div>
                  <span className="text-gray-900 block mb-1">Position</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {userDetails?.position?.length ? (
                      userDetails.position.map((pos, i) => (
                        <span
                          key={i}
                          className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium shadow-sm"
                        >
                          {pos}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-400 italic">N/A</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow duration-300">
              <h3 className="text-lg font-semibold mb-5 text-purple-600 border-b">
                Additional Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-10 text-gray-700">
                <div>
                  <span className="text-gray-900 block mb-1">Role</span>
                  <div className="font-medium text-gray-400">
                    {userDetails?.role || "N/A"}
                  </div>
                </div>
                <div>
                  <span className="text-gray-900 block mb-1">
                    Date of Birth
                  </span>
                  <div className="font-medium text-gray-400">
                    {userDetails?.dateOfBirth?.slice(0, 10) || "N/A"}
                  </div>
                </div>
                <div>
                  <span className="text-gray-900 block mb-1">Employee ID</span>
                  <div className="font-medium text-gray-400">
                    {userDetails?.employeeID || "N/A"}
                  </div>
                </div>
                <div>
                  <span className="text-gray-900 block mb-1">
                    Monthly Salary
                  </span>
                  <div className="font-medium text-gray-400">
                    {userDetails?.monthlySalary || "N/A"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => setShowPasswordModal(true)}
              className="px-8 py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white font-semibold rounded-xl shadow-lg hover:from-pink-600 hover:to-purple-600 transition-all"
            >
              Reset Password
            </button>
          </div>

          {/* Password Modal */}
          {showPasswordModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
              <div className="bg-white rounded-xl w-full max-w-lg p-6 shadow-2xl relative">
                <h3 className="text-xl font-bold mb-5 text-gray-900">
                  Reset Your Password
                </h3>

                {["currentPassword", "newPassword", "confirmPassword"].map(
                  (field) => (
                    <div key={field} className="relative mb-4">
                      <input
                        type={showPassword[field] ? "text" : "password"}
                        name={field}
                        placeholder={
                          field === "confirmPassword"
                            ? "Re-enter Password"
                            : field.replace(/([A-Z])/g, " $1")
                        }
                        value={passwordData[field]}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 border rounded-lg ${
                          errors[field] ? "border-red-500" : "border-gray-300"
                        } focus:ring-2 focus:ring-pink-400 focus:border-pink-400`}
                      />
                      <button
                        type="button"
                        onClick={() => toggleShowPassword(field)}
                        className="absolute inset-y-0 end-0 flex items-center pe-3 text-gray-500"
                      >
                        {showPassword[field] ? <FaEyeSlash /> : <FaEye />}
                      </button>
                      {errors[field] && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors[field]}
                        </p>
                      )}
                    </div>
                  )
                )}

                <div className="flex justify-end gap-4 mt-6">
                  <button
                    onClick={updatePassword}
                    disabled={loading}
                    className="flex items-center px-5 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-xl shadow-lg"
                  >
                    {loading && <ImSpinner3 className="animate-spin mr-2" />}
                    Update
                  </button>
                  <button
                    onClick={() => setShowPasswordModal(false)}
                    className="px-5 py-2 bg-gray-300 text-black rounded-xl hover:bg-gray-400 transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </DashLayout>
    </Layout>
  );
};

export default StaffMyProfile;
